import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X, MapPin, Users, Calendar, CloudSun, ArrowRight, Images } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  xamplinApi,
  entityIdOf,
  deriveEventStatus,
  PURPOSE_BY_ID,
  type XpStructureEvent,
} from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';

/**
 * Event detail drawer (build-v2 G5) — opens from the planner; mirrors the
 * prototype `EventDetailDrawer`. Shows venue/dates/attendance/budget, a weather
 * estimate, the staffing roster, the branded assets grouped by Purpose, and
 * mode-aware actions. All real data (assets via xp-photo feed).
 */
const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f',
};
const STATUS_DOT: Record<string, string> = { plan: '#9b7fe8', live: '#4dd28b', completed: '#5aa9ff' };

/** Seasonal weather estimate from month + latitude band — labelled as an
 * estimate (no live weather API wired). Honest placeholder for venue planning. */
function weatherEstimate(startMs: number, lat: number | null | undefined): { icon: string; label: string } {
  if (!Number.isFinite(startMs)) return { icon: '—', label: 'sin fecha' };
  const month = new Date(startMs).getUTCMonth();
  const warm = lat == null || lat < 33; // south of ~Atlanta = warm year-round
  const summer = month >= 4 && month <= 9;
  if (warm && summer) return { icon: '☀️', label: '~31°C · soleado (est.)' };
  if (warm) return { icon: '🌤️', label: '~24°C · templado (est.)' };
  if (summer) return { icon: '⛅', label: '~26°C · parcial (est.)' };
  return { icon: '🌧️', label: '~12°C · fresco (est.)' };
}

export function EventDetailDrawer({
  event,
  projectId,
  onClose,
}: {
  event: XpStructureEvent;
  projectId: number;
  onClose: () => void;
}) {
  const eventId = entityIdOf(event.id);
  const status = deriveEventStatus(event);
  const start = event.startDate ? Date.parse(event.startDate) : NaN;
  const weather = weatherEstimate(start, event.lat);
  const stations = event.stations ?? [];
  const reporters = event.reporters ?? [];

  const photosQ = useQuery({
    queryKey: queryKeys.xamplin.photoFeed({ eventId, scope: 'drawer' }),
    queryFn: () => xamplinApi.photos.feed({ eventId, limit: 40 }),
    enabled: eventId > 0,
  });
  const photos = photosQ.data?.items ?? [];

  return createPortal(
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-2 border-b border-[var(--color-xk-border)] p-4">
          <span className="mt-1.5 h-2.5 w-2.5 flex-none rounded-full" style={{ background: STATUS_DOT[status] }} />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-[var(--color-xk-lavender)]">{event.name}</h2>
            <div className="mono mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--color-xk-text-muted)]">
              <span className="uppercase">{status}</span>
              {Number.isFinite(start) && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(start).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}
                  </span>
                </>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          {/* Venue + weather */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={<MapPin className="h-3.5 w-3.5" />} label="Venue" value={event.location || '—'} />
            <Field icon={<CloudSun className="h-3.5 w-3.5" />} label="Clima" value={`${weather.icon} ${weather.label}`} />
            <Field icon={<Users className="h-3.5 w-3.5" />} label="Asistencia" value={(event.attendance ?? 0).toLocaleString()} />
            <Field label="Presupuesto" value={`$${(event.budget ?? 0).toLocaleString()}`} />
          </div>

          {/* Stations */}
          <Section title={`Stations · ${stations.length}`}>
            {stations.length === 0 ? (
              <Empty>Sin stations.</Empty>
            ) : (
              <div className="flex flex-col gap-1">
                {stations.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-2.5 py-1.5 text-xs text-[var(--color-xk-lavender)]">
                    <MapPin className="h-3 w-3 flex-none text-[var(--color-xk-accent)]" />
                    <span className="flex-1 truncate">{s.name}</span>
                    {s.purpose && (
                      <span className="mono rounded-full px-1.5 py-0.5 text-[8px] font-bold text-white" style={{ background: PURPOSE_COLOR[s.purpose] ?? 'var(--color-xk-border)' }}>
                        {s.purpose}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Reporters */}
          <Section title={`Team · ${reporters.length}`}>
            {reporters.length === 0 ? (
              <Empty>Sin reporters asignados.</Empty>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {reporters.map((r) => (
                  <span key={r.id} className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]">
                    👤 {r.name || r.email}
                  </span>
                ))}
              </div>
            )}
          </Section>

          {/* Branded assets by purpose */}
          <Section title="Branded assets">
            {photosQ.isLoading ? (
              <Empty>Cargando…</Empty>
            ) : photos.length === 0 ? (
              <Empty>Sin assets de campo.</Empty>
            ) : (
              <div className="flex flex-col gap-2">
                {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) => {
                  const items = photos.filter((ph) => ph.purposeId && PURPOSE_BY_ID[ph.purposeId] === p);
                  if (items.length === 0) return null;
                  return (
                    <div key={p} className="flex items-center gap-2">
                      <span className="mono w-14 flex-none rounded-full px-1.5 py-0.5 text-center text-[8px] font-bold text-white" style={{ background: PURPOSE_COLOR[p] }}>
                        {p}
                      </span>
                      <div className="scrollbar-none flex flex-1 gap-1 overflow-x-auto">
                        {items.slice(0, 10).map((ph) => (
                          <img key={ph.id} src={ph.image} alt="" loading="lazy" className="h-12 w-12 flex-none rounded-md object-cover" />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-1">
            <Link
              to={`/projects/${projectId}/events/${eventId}`}
              className="flex items-center justify-center gap-2 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-white"
            >
              Measure dashboard <ArrowRight className="h-4 w-4" />
            </Link>
            {photos.length > 0 && (
              <Link
                to="/photos"
                className="flex items-center justify-center gap-2 rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-sm font-semibold text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]"
              >
                <Images className="h-4 w-4" /> Ver fotos del evento
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2">
      <div className="mono flex items-center gap-1 text-[9px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-[var(--color-xk-lavender)]">{value}</div>
    </div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">{title}</h3>
      {children}
    </div>
  );
}
function Empty({ children }: { children: React.ReactNode }) {
  return <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">{children}</span>;
}
